import fetchWithAuth from '@/lib/apis';
import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { isAuthenticated } from '@/lib/auth';
import { isOnboardingComplete, getOnboardingRedirectPath } from '@/lib/auth-navigation';
import { apiEndpoint } from '@/lib/consts';
import type { GoogleAdsAccount, OrganizationDataWithMembership } from '@/lib/types';

import { useAtomValue, useSetAtom } from 'jotai';
import { campaignsQueryOptions, userProfileQueryOptions, organizationsQueryOptions, googleAdsAccountsQueryOptions, GOOGLE_ADS_ACCOUNTS_QUERY_KEY } from '@/lib/query-options';
import { getCookie, getUserProfileFromToken } from '@/lib/auth';
import { campaignsDataAtom, campaignsMetaAtom, googleAdsAccountAtom, googleAdsAccountResolvedAtom, organizationDataAtom, isLoadingCampaignsAtom, campaignsErrorAtom } from '@/store';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ location, context }) => {
    // Check authentication first
    if (!isAuthenticated()) {
      throw redirect({
        to: '/signin',
        search: {
          redirect: location.href,
        },
      });
    }

    // For welcome routes, redirect to /notifications if onboarding is complete
    const isWelcomeRoute = location.pathname.startsWith('/welcome');

    // Check organization membership and onboard if incomplete
    const token = getCookie('access_token');
    const user = getUserProfileFromToken(token);
    if (!user.impersonating_email) {
      // Fetch data outside try-catch to allow redirects to bubble up naturally
      const queryClient = context.queryClient;
      
      let organizations;
      
      let userData;
      try {
        // Prefetch all required data before rendering
        userData = await queryClient.ensureQueryData(userProfileQueryOptions());
        [organizations] = await Promise.all([
          queryClient.ensureQueryData(organizationsQueryOptions()),
          queryClient.ensureQueryData(googleAdsAccountsQueryOptions())
        ]);
      } catch (error) {
        // Only catch data fetching errors, not redirects
        throw new Error('Server Unavailable');
      }
      
      if (!organizations) {
        throw new Error('Error fetching organizations');
      }
      
      // Handle redirects outside try-catch so they bubble up to the router
      if (organizations.length === 0) {
        // Allow users with no organizations to access welcome routes for onboarding
        if (!isWelcomeRoute) {
          throw redirect({
            to: '/welcome/organization-name',
          });
        }
        // Continue - users with no orgs can access welcome routes
      }
      
      // Only process organization logic if user has organizations
      if (organizations.length > 0) {
        // Check if user has only pending invites (no active organizations)
        const activeOrgs = organizations.filter(org => 
          org.membership?.status === 'active'
        );
        const pendingOrgs = organizations.filter(org => 
          org.membership?.status === 'pending'
        );
        
        
        // If user has pending invites, redirect to pending-invites page
        if (pendingOrgs.length > 0 && location.pathname !== '/pending-invites') {
          throw redirect({
            to: '/pending-invites',
          });
        }
        
        // If no active organizations and we're not on pending-invites page, error
        if (activeOrgs.length === 0 && location.pathname !== '/pending-invites') {
          throw new Error('No active organization memberships found');
        }
        
        // Check if user has any completed organizations (not just the first one)
        const hasCompletedOrg = activeOrgs.some(org => isOnboardingComplete(org));
        
        if (!hasCompletedOrg) {
          // If no organizations are complete, redirect to onboarding (only if not already on welcome or pending-invites)
          if (!isWelcomeRoute && location.pathname !== '/pending-invites') {
            throw redirect({
              to: getOnboardingRedirectPath(),
            });
          }
        } else {
          // Users with at least one completed organization can navigate freely
          // They can access welcome routes to create new organizations or leave them at any time
        }
        
        // Select org for prefetching - prefer user's last selected org, fallback to first active
        let selectedOrg = null;
        if (userData?.last_organization_id) {
          selectedOrg = activeOrgs.find(org => org.id === userData.last_organization_id);
        }
        if (!selectedOrg) {
          selectedOrg = activeOrgs[0] || organizations[0];
        }
        const firstOrg = selectedOrg;

        // Auto-select single Google Ads account if no selection exists
        let selectedAccountId = firstOrg?.membership?.last_selected_googleads_account_id;

        // If no saved selection and only one account exists, auto-select it (only for active orgs)
        if (!selectedAccountId && firstOrg?.membership?.status === 'active') {
          const _googleAdsAccounts = queryClient.getQueryData<GoogleAdsAccount[]>(GOOGLE_ADS_ACCOUNTS_QUERY_KEY);

          if (_googleAdsAccounts && _googleAdsAccounts.length === 1) {
            const singleAccount = _googleAdsAccounts[0];
            selectedAccountId = singleAccount.googleads_account_id;

            // Persist the auto-selection to the backend
            try {
              let orgCurrency = firstOrg.default_currency;

              // Set org currency from the account first — set-selected will 400 if currency is missing
              if (!orgCurrency && singleAccount.currency) {
                const currencyResponse = await fetchWithAuth(`${apiEndpoint}/organizations/${firstOrg.id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ default_currency: singleAccount.currency })
                });
                if (currencyResponse.ok) {
                  orgCurrency = singleAccount.currency;
                }
              }

              const response = await fetchWithAuth(`${apiEndpoint}/google/accounts/set-selected`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  googleads_account_id: selectedAccountId,
                  organization_id: firstOrg.id
                })
              });

              if (response.ok) {
                const updatedOrg = {
                  ...firstOrg,
                  default_currency: orgCurrency,
                  membership: {
                    ...firstOrg.membership,
                    last_selected_googleads_account_id: selectedAccountId
                  }
                };

                // Update the organizations cache
                const orgsCache = queryClient.getQueryData<OrganizationDataWithMembership[]>(['organizations']);

                if (orgsCache) {
                  const updatedOrgs = orgsCache.map(org =>
                    org.id === firstOrg.id ? updatedOrg : org
                  );
                  queryClient.setQueryData(['organizations'], updatedOrgs);
                }
              }
            } catch (error) {
              // Ignore auto-selection errors - non-critical
            }
          }
        }

        // Only prefetch campaigns for active organizations (not pending invitations)
        if (firstOrg?.id && selectedAccountId && firstOrg?.membership?.status === 'active') {
          try {
            await queryClient.prefetchQuery(
              campaignsQueryOptions(firstOrg.id, selectedAccountId)
            );
          } catch (error) {
            // Ignore campaign prefetch errors - non-critical
            console.error('Failed to prefetch campaigns:', error);
          }
        }
      } else {
        // User has no organizations - they should be in welcome flow
      }
    }
  },

  component: () => <AppShell />,
});

function AppShell() {
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const organizationData = useAtomValue(organizationDataAtom);
  const setOrganizationData = useSetAtom(organizationDataAtom);
  const setGoogleAdsAccount = useSetAtom(googleAdsAccountAtom);
  const setGoogleAdsAccountResolved = useSetAtom(googleAdsAccountResolvedAtom);
  
  // Use suspense queries - data is guaranteed to exist from beforeLoad
  const { data: userData } = useSuspenseQuery(userProfileQueryOptions());
  const { data: organizations } = useSuspenseQuery(organizationsQueryOptions());
  const { data: googleAdsAccounts } = useSuspenseQuery(googleAdsAccountsQueryOptions());

  
  // Initialize organization data only once 
  useEffect(() => {
    if (!organizationData && organizations.length > 0) {
      let selectedOrg = null;
      
      // Use saved organization from database
      if (userData?.last_organization_id) {
        selectedOrg = organizations.find(org => org.id === userData.last_organization_id);
      }
      
      // Fallback to first organization
      if (!selectedOrg) {
        selectedOrg = organizations[0];
      }
      
      setOrganizationData(selectedOrg);
    }
  }, [organizations.length, userData?.last_organization_id, organizationData, setOrganizationData]); // Only re-run if organizations array length changes or user changes

  // Track previous org ID to detect org switches
  const prevOrgIdRef = useRef<number | undefined>(organizationData?.id);

  // Initialize Google Ads account based on current organization
  useEffect(() => {
    const orgChanged = prevOrgIdRef.current !== undefined && prevOrgIdRef.current !== organizationData?.id;
    prevOrgIdRef.current = organizationData?.id;

    if (orgChanged && !organizationData?.membership?.last_selected_googleads_account_id && googleAdsAccount) {
      // Clear stale account when switching to an org with no saved selection
      setGoogleAdsAccount(null);
    } else if (!googleAdsAccount && googleAdsAccounts.length > 0 && organizationData?.membership?.last_selected_googleads_account_id) {
      // Initialize from saved selection
      const selectedAccount = googleAdsAccounts.find(account =>
        account.googleads_account_id === organizationData.membership.last_selected_googleads_account_id
      );
      if (selectedAccount) {
        setGoogleAdsAccount(selectedAccount);
      }
    } else if (!googleAdsAccount && googleAdsAccounts.length === 1) {
      // Auto-select when only one account exists and no saved selection (e.g., no org or impersonation)
      setGoogleAdsAccount(googleAdsAccounts[0]);
    }
    // Selection logic has now run for the loaded org (accounts arrive via suspense, orgs
    // via the effect above) — from here a null account genuinely means "none selected",
    // so Google-data surfaces can stop showing a loading state (#5820).
    if (organizationData || organizations.length === 0) {
      setGoogleAdsAccountResolved(true);
    }
  }, [googleAdsAccounts.length, organizationData, organizations.length, organizationData?.membership?.last_selected_googleads_account_id, googleAdsAccount, setGoogleAdsAccount, setGoogleAdsAccountResolved, googleAdsAccounts]); // Re-run if accounts or org selection changes
  

  // Campaigns query - organizations guaranteed to be available from beforeLoad
  const {
    isLoading: campaignsLoading,
    data: campaigns = [],
    error,
  } = useQuery({
    ...campaignsQueryOptions(organizationData?.id, googleAdsAccount?.googleads_account_id),
    enabled: !!organizationData?.id && !!googleAdsAccount?.googleads_account_id
  });

  // Meta from the same cached query payload — second observer projecting
  // the meta field (#4884, replaces the window.__campaignsMeta global).
  const { data: meta } = useQuery({
    ...campaignsQueryOptions(organizationData?.id, googleAdsAccount?.googleads_account_id),
    enabled: !!organizationData?.id && !!googleAdsAccount?.googleads_account_id,
    select: (data) => data.meta,
  });


  const setCampaignsData = useSetAtom(campaignsDataAtom);
  const setCampaignsMeta = useSetAtom(campaignsMetaAtom);
  const setIsLoadingCampaigns = useSetAtom(isLoadingCampaignsAtom);
  const setCampaignsError = useSetAtom(campaignsErrorAtom);

  // useLoadingOverlay(isLoading); // Disabled - don't show loading overlay

  // Mirror the campaigns query into the legacy atoms consumers still read. Refetch is no
  // longer stored as a closure in an atom — useRefetchCampaigns invalidates the query and
  // this effect (keyed on `campaigns`) repopulates the atom when fresh data arrives.
  useEffect(() => {
    setCampaignsData(campaigns);
    setCampaignsMeta(meta || null);
    setIsLoadingCampaigns(campaignsLoading);
    setCampaignsError(error as Error | null);
  }, [campaigns, meta, campaignsLoading, error, setCampaignsData, setCampaignsMeta, setIsLoadingCampaigns, setCampaignsError]);

  return (
    <div className='min-h-full'>
      <main>
        <Outlet />
      </main>
    </div>
  );
}
